import i18next, {DefaultNamespace, Namespace, TOptions} from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import {initReactI18next, Trans as i18nTrans, TransProps} from 'react-i18next';
import type {ReactElement} from 'react';

import {isTestEnv} from 'common/report/util/env';
import {reporter} from 'common/report/error-reporting';
import {localStorageManager} from 'common/contexts/storage-manager';

import type {AnyScopedLocalizationStore, LocalizationStore} from './store';
import {LOCALES} from './locales';
import {
  loadAllRegisteredScopeTranslations,
  loadScopesTranslations,
  onScopeTranslationLoadersRegistered,
} from './scope-translations';

const STORAGE_KEY = `selectedLanguage`;
const DEFAULT_NS = `translation`;
const ENGLISH = `en`;

function getDefaultLocale() {
  const stored = typeof window !== `undefined` ? localStorageManager.getItem(STORAGE_KEY) : null;
  if (!isTestEnv() && stored) {
    return stored;
  }

  return `en`;
}

type TranslationBundle = Record<string, string>;

/**
 * Loads the translations of the scopes registered so far, so that switching language renders them at once. Scopes that
 * register later (route chunks) load theirs on registration. The hand-written overrides are already merged into them.
 */
async function loadLocaleBundle(language: string): Promise<TranslationBundle> {
  if (language === ENGLISH) {
    return {};
  }
  return loadAllRegisteredScopeTranslations(language);
}

const initialized = i18next
  .use(resourcesToBackend(loadLocaleBundle))
  .use(initReactI18next) // bind react-i18next to the instance
  .init({
    lng: getDefaultLocale(),
    fallbackLng: `en`,
    // not supporting saving missing, but needs to be true to enable missingKey event
    saveMissing: true,
    defaultNS: DEFAULT_NS,
    interpolation: {
      // Translated text with user input is never rendered directly in the dom (React already handles xss sanitization).
      // Double-escaping leads to rendering issues for names with special characters
      escapeValue: false,
    },
  });

// https://www.i18next.com/translation-function/formatting#adding-custom-format-function
i18next.services.formatter?.add(`lowercase`, (value) => {
  return value.toLowerCase();
});

/** Replaces previously added translations of a scope (a re-registered scope may have new text). */
function addScopeTranslations(language: string, translations: TranslationBundle) {
  i18next.addResourceBundle(language, DEFAULT_NS, translations, true, true);
}

const scopesAwaitingTranslations = new Set<string>();
const translationLoadsInFlight = new Set<Promise<void>>();

function trackTranslationLoad(load: Promise<void>): Promise<void> {
  translationLoadsInFlight.add(load);
  void load.finally(() => translationLoadsInFlight.delete(load));
  return load;
}

/**
 * Loads and adds the translations of the scopes that registered since the last flush in one go: a route chunk registers
 * many scopes in the same tick, and they mostly share a bundle that should be flattened and added to i18next once.
 */
function flushScopesAwaitingTranslations() {
  const scopeIds = [...scopesAwaitingTranslations];
  scopesAwaitingTranslations.clear();
  const language = i18next.language || i18next.options.lng;
  if (scopeIds.length > 0 && language && language !== ENGLISH) {
    void trackTranslationLoad(
      loadScopesTranslations(scopeIds, language).then((translations) => addScopeTranslations(language, translations)),
    );
  }
}

// A scope registered while a foreign language is active (its route chunk was just evaluated) fetches its translations.
onScopeTranslationLoadersRegistered((scopeId) => {
  const language = i18next.language || i18next.options.lng;
  if (language && language !== ENGLISH) {
    if (scopesAwaitingTranslations.size === 0) {
      queueMicrotask(flushScopesAwaitingTranslations);
    }
    scopesAwaitingTranslations.add(scopeId);
  }
});

let pendingLanguageTranslations: Promise<void> = Promise.resolve();

i18next.on(`languageChanged`, (lng) => {
  if (typeof window !== `undefined`) {
    localStorageManager.setItem(STORAGE_KEY, lng);
  }
  // i18next loads a language's bundle once; scopes registered since (route chunks) were not in it
  if (lng !== ENGLISH) {
    pendingLanguageTranslations = trackTranslationLoad(
      loadAllRegisteredScopeTranslations(lng).then((translations) => addScopeTranslations(lng, translations)),
    );
  }
});

i18next.on(`missingKey`, (_lng, _namespace, key, _fallbackValue) => {
  reporter.error(`Missing i18n entry for ${key}`);
});

/** Resolves once the language's translations of every scope registered so far are in i18next. */
export async function changeLanguage(code: string) {
  await i18next.changeLanguage(code);
  await pendingLanguageTranslations;
}

export const SCOPE_TRANSLATIONS_WAIT_LIMIT_MS = 3000;

/**
 * Resolves once the translations of every scope registered so far are in i18next, so a lazily loaded route can keep its
 * loader up instead of rendering English first. Gives up after a few seconds; late translations still re-render.
 */
export async function scopeTranslationsReady(): Promise<void> {
  if (scopesAwaitingTranslations.size > 0) {
    flushScopesAwaitingTranslations();
  }
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      // a failed load renders English rather than failing the route
      Promise.all([initialized, ...translationLoadsInFlight]).catch(() => undefined),
      new Promise((resolve) => {
        timeout = setTimeout(resolve, SCOPE_TRANSLATIONS_WAIT_LIMIT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timeout);
  }
}

// this is a little hack that makes i18next conditional types work w/o strictNullChecks enabled
type KeyPrefix = 0;

declare const LOCALE_SAFE_BRAND: unique symbol;

/**
 * String safe to display in any locale — either produced by `t(...)` or marked
 * safe via `markLocaleSafe` because the source guarantees display-safety
 * regardless of locale (e.g. a backend that honors Accept-Language, an empty
 * string, a proper noun). Assignable to plain `string`, so it flows through
 * the rest of the codebase unchanged.
 *
 * Branded so that `StrictTranslatable` callbacks and props typed as
 * `LocaleSafeString` can't be satisfied by a raw untranslated literal.
 */
export type LocaleSafeString = string & {readonly [LOCALE_SAFE_BRAND]: true};

/** Escape hatch for strings whose source guarantees locale-safety. */
export function markLocaleSafe(value: string): LocaleSafeString {
  return value as LocaleSafeString;
}

/**
 * The store a translatable is run with: whichever store `print` was invoked on. A scoped store only accepts the
 * keys of its own scope, so a translatable written elsewhere translates with `translatable(scope, render)`.
 */
export type TranslationStore = LocalizationStore | AnyScopedLocalizationStore;
export type Translatable = string | ((i18n: TranslationStore) => string);
export type StrictTranslatable = (i18n: TranslationStore) => LocaleSafeString;

// // if we ever get to strictNullChecks, can remove this retyping and use react-i18next's type
type TransFunc = <
  TranslateFunction extends (key: never, opts?: TOptions) => LocaleSafeString,
  Ns extends Namespace = DefaultNamespace,
  KPrefix = KeyPrefix,
  TContext extends string | undefined = undefined,
  TOpt extends TOptions & {context?: TContext} = {context: TContext},
  E = React.HTMLProps<HTMLDivElement>,
>(
  props: Omit<TransProps<string, Ns, KPrefix, TContext, TOpt, E>, `i18nKey` | `t`> & {
    /** The `t` of a scoped store (keys of that scope) or its `translateKey` (keys from `scope.qualify`). */
    t: TranslateFunction;
    i18nKey: Parameters<TranslateFunction>[0];
  },
) => ReactElement;

export const Trans = i18nTrans as TransFunc;

export {i18next, LOCALES};
